import React, { useState, useMemo, useCallback } from 'react';
import { useCRM } from '../context/CRMContext';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, FunnelChart, Funnel,
  LabelList, Tooltip, Legend, XAxis, YAxis, CartesianGrid,
  ResponsiveContainer, ScatterChart, Scatter, ZAxis,
} from 'recharts';

// ─── Palette ──────────────────────────────────────────────────────────────────
const COLORS = ['#7c3aed','#1877f2','#10b981','#f59e0b','#ef4444','#06b6d4','#8b5cf6','#ec4899','#14b8a6','#f97316'];
const GRADIENTS = [
  ['#7c3aed','#a78bfa'], ['#1877f2','#60a5fa'], ['#10b981','#6ee7b7'],
  ['#f59e0b','#fcd34d'], ['#ef4444','#fca5a5'], ['#06b6d4','#67e8f9'],
];

// ─── Date helpers ─────────────────────────────────────────────────────────────
function toTs(val) {
  if (!val) return 0;
  if (val?.toDate) return val.toDate().getTime();
  if (val instanceof Date) return val.getTime();
  return new Date(val).getTime();
}

function daysBucket(leads, days) {
  const now = Date.now();
  const cutoff = now - days * 86400000;
  const buckets = {};
  for (let d = days - 1; d >= 0; d--) {
    const t = new Date(now - d * 86400000);
    const key = t.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    buckets[key] = 0;
  }
  leads.forEach(l => {
    const ts = toTs(l.createdAt);
    if (ts >= cutoff) {
      const key = new Date(ts).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
      if (key in buckets) buckets[key] = (buckets[key] || 0) + 1;
    }
  });
  return Object.entries(buckets).map(([date, count]) => ({ date, count }));
}

function weekBuckets(leads, weeks) {
  const now = Date.now();
  const buckets = {};
  for (let w = weeks - 1; w >= 0; w--) {
    const t = new Date(now - w * 7 * 86400000);
    const key = 'W' + getWeekNum(t) + ' ' + t.getFullYear().toString().slice(2);
    buckets[key] = 0;
  }
  leads.forEach(l => {
    const ts = toTs(l.createdAt);
    if (ts >= now - weeks * 7 * 86400000) {
      const d = new Date(ts);
      const key = 'W' + getWeekNum(d) + ' ' + d.getFullYear().toString().slice(2);
      if (key in buckets) buckets[key] = (buckets[key] || 0) + 1;
    }
  });
  return Object.entries(buckets).map(([week, count]) => ({ week, count }));
}
function getWeekNum(d) {
  const s = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - s) / 86400000 + s.getDay() + 1) / 7);
}

function countBy(arr, key) {
  const map = {};
  arr.forEach(item => {
    const v = (typeof key === 'function' ? key(item) : item[key]) || 'Unknown';
    map[v] = (map[v] || 0) + 1;
  });
  return Object.entries(map).sort((a, b) => b[1] - a[1]);
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KPICard({ label, value, sub, color = '#7c3aed', icon }) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(0,0,0,0.07)', padding: '18px 20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)', flex: 1, minWidth: 140 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
        {icon && <div style={{ fontSize: 18 }}>{icon}</div>}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

// ─── Chart wrapper ────────────────────────────────────────────────────────────
function ChartCard({ title, subtitle, children, action }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(0,0,0,0.07)', padding: '20px 22px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{subtitle}</div>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', borderRadius: 9, padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
      {label && <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ fontSize: 12, color: p.color || '#fff', fontWeight: 600 }}>
          {p.name !== undefined ? `${p.name}: ` : ''}{p.value}
        </div>
      ))}
    </div>
  );
};

// ─── Date range filter bar ────────────────────────────────────────────────────
const RANGES = [
  { id: '3d',  label: '3D',  days: 3 },
  { id: '7d',  label: '7D',  days: 7 },
  { id: '14d', label: '14D', days: 14 },
  { id: '30d', label: '30D', days: 30 },
  { id: '90d', label: '90D', days: 90 },
  { id: 'all', label: 'All',  days: 9999 },
];

function RangeBar({ range, setRange }) {
  return (
    <div style={{ display: 'flex', gap: 4, background: 'rgba(0,0,0,0.04)', borderRadius: 10, padding: 3 }}>
      {RANGES.map(r => (
        <button key={r.id} onClick={() => setRange(r.id)}
          style={{ padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
            background: range === r.id ? '#fff' : 'transparent',
            color: range === r.id ? '#7c3aed' : '#64748b',
            boxShadow: range === r.id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}>
          {r.label}
        </button>
      ))}
    </div>
  );
}

// ─── SECTION: Lead Overview ───────────────────────────────────────────────────
function LeadOverviewReport({ leads, users, pipelineStages, range }) {
  const days = RANGES.find(r => r.id === range)?.days || 30;
  const cutoff = Date.now() - days * 86400000;
  const filtered = useMemo(() => leads.filter(l => days >= 9999 || toTs(l.createdAt) >= cutoff), [leads, days, cutoff]);

  // KPIs
  const total        = filtered.length;
  const thisWeek     = filtered.filter(l => toTs(l.createdAt) >= Date.now() - 7 * 86400000).length;
  const hot          = filtered.filter(l => l.priority === 'hot').length;
  const converted    = filtered.filter(l => {
    const stage = pipelineStages.find(s => s.id === l.status || s.name === l.status);
    return stage?.name?.toLowerCase().includes('enroll') || stage?.name?.toLowerCase().includes('admit') || stage?.name?.toLowerCase().includes('converted');
  }).length;
  const convRate     = total > 0 ? ((converted / total) * 100).toFixed(1) : 0;

  // Trend chart
  const trendData = useMemo(() => daysBucket(filtered, Math.min(days, 30)), [filtered, days]);

  // Source breakdown
  const sourceData = useMemo(() => countBy(filtered, 'source').slice(0,8).map(([name, value], i) => ({ name, value, fill: COLORS[i % COLORS.length] })), [filtered]);

  // Status funnel
  const funnelData = useMemo(() => {
    const stages = pipelineStages.slice(0, 6);
    return stages.map((s, i) => ({
      name: s.name,
      value: filtered.filter(l => l.status === s.id || l.status === s.name).length,
      fill: COLORS[i % COLORS.length],
    })).filter(d => d.value > 0);
  }, [filtered, pipelineStages]);

  // Priority distribution
  const priorityData = useMemo(() => countBy(filtered, 'priority').map(([name, value], i) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value, fill: COLORS[i] })), [filtered]);

  // Country breakdown
  const countryData = useMemo(() => {
    const map = {};
    filtered.forEach(l => {
      const countries = l.countries?.length ? l.countries : (l.country ? [l.country] : ['Unknown']);
      countries.forEach(c => { map[c] = (map[c] || 0) + 1; });
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([name, value], i) => ({ name, value, fill: COLORS[i % COLORS.length] }));
  }, [filtered]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* KPIs */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <KPICard label="Total Leads" value={total} sub={`Past ${days >= 9999 ? 'all time' : days + ' days'}`} color="#7c3aed" icon="👥" />
        <KPICard label="This Week" value={thisWeek} sub="Last 7 days" color="#1877f2" icon="📅" />
        <KPICard label="Hot Leads" value={hot} sub={`${total > 0 ? ((hot/total)*100).toFixed(0) : 0}% of total`} color="#ef4444" icon="🔥" />
        <KPICard label="Conversion Rate" value={convRate + '%'} sub={`${converted} converted`} color="#10b981" icon="🎯" />
      </div>

      {/* Trend + Source */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
        <ChartCard title="Lead Volume Trend" subtitle="Leads added over time">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trendData}>
              <defs>
                <linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.18}/>
                  <stop offset="95%" stopColor="#7c3aed" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="count" name="Leads" stroke="#7c3aed" strokeWidth={2.5} fill="url(#grad1)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Lead Sources" subtitle="Where leads come from">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={sourceData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                dataKey="value" paddingAngle={2}>
                {sourceData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Funnel + Priority + Country */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
        <ChartCard title="Pipeline Funnel" subtitle="Leads by stage">
          <ResponsiveContainer width="100%" height={220}>
            <FunnelChart>
              <Tooltip content={<CustomTooltip />} />
              <Funnel dataKey="value" data={funnelData} isAnimationActive>
                {funnelData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                <LabelList position="right" fill="#475569" stroke="none" dataKey="name" style={{ fontSize: 11 }} />
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Priority Split" subtitle="Hot / Warm / Cold">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={priorityData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: 10 }}>
                {priorityData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Country Breakdown" subtitle="Top destinations">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={countryData} layout="vertical" margin={{ left: 0, right: 20 }}>
              <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#475569' }} width={70} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" name="Leads" radius={[0, 5, 5, 0]}>
                {countryData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

// ─── SECTION: Counselor Performance ──────────────────────────────────────────
function CounselorReport({ leads, users, pipelineStages, range }) {
  const days = RANGES.find(r => r.id === range)?.days || 30;
  const cutoff = Date.now() - days * 86400000;
  const filtered = useMemo(() => leads.filter(l => days >= 9999 || toTs(l.createdAt) >= cutoff), [leads, days, cutoff]);

  const counselorStats = useMemo(() => {
    const map = {};
    filtered.forEach(l => {
      const name = l.assignedTo || 'Unassigned';
      if (!map[name]) map[name] = { name, total: 0, hot: 0, converted: 0, sources: {} };
      map[name].total++;
      if (l.priority === 'hot') map[name].hot++;
      const stage = pipelineStages.find(s => s.id === l.status || s.name === l.status);
      if (stage?.name?.toLowerCase().includes('enroll') || stage?.name?.toLowerCase().includes('admit') || stage?.name?.toLowerCase().includes('converted')) map[name].converted++;
      const src = l.source || 'Unknown';
      map[name].sources[src] = (map[name].sources[src] || 0) + 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filtered, pipelineStages]);

  const barData = counselorStats.map(c => ({
    name: c.name.split(' ')[0],
    Total: c.total,
    Hot: c.hot,
    Converted: c.converted,
  }));

  // Radar data
  const radarData = useMemo(() => {
    const top5 = counselorStats.slice(0, 5);
    if (!top5.length) return [];
    const max = { total: 1, hot: 1, converted: 1 };
    top5.forEach(c => {
      max.total = Math.max(max.total, c.total);
      max.hot = Math.max(max.hot, c.hot);
      max.converted = Math.max(max.converted, c.converted);
    });
    return [
      { metric: 'Volume', ...Object.fromEntries(top5.map(c => [c.name.split(' ')[0], Math.round((c.total / max.total) * 100)])) },
      { metric: 'Hot Leads', ...Object.fromEntries(top5.map(c => [c.name.split(' ')[0], Math.round((c.hot / max.hot) * 100)])) },
      { metric: 'Conversions', ...Object.fromEntries(top5.map(c => [c.name.split(' ')[0], Math.round((c.converted / (max.converted || 1)) * 100)])) },
      { metric: 'Conv Rate', ...Object.fromEntries(top5.map(c => [c.name.split(' ')[0], c.total > 0 ? Math.round((c.converted / c.total) * 100) : 0])) },
    ];
  }, [counselorStats]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* KPI row */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <KPICard label="Active Counselors" value={counselorStats.filter(c => c.name !== 'Unassigned').length} color="#7c3aed" icon="👤" />
        <KPICard label="Top Performer" value={counselorStats[0]?.name?.split(' ')[0] || '—'} sub={`${counselorStats[0]?.total || 0} leads`} color="#1877f2" icon="🏆" />
        <KPICard label="Best Conversion" value={(() => { const best = counselorStats.filter(c => c.total >= 3).sort((a,b) => (b.converted/b.total) - (a.converted/a.total))[0]; return best ? best.name.split(' ')[0] : '—'; })()} sub="By rate" color="#10b981" icon="🎯" />
        <KPICard label="Unassigned" value={counselorStats.find(c => c.name === 'Unassigned')?.total || 0} sub="Need assignment" color="#f59e0b" icon="⚠️" />
      </div>

      {/* Bar chart + Radar */}
      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 16 }}>
        <ChartCard title="Counselor Comparison" subtitle="Total leads, hot leads, conversions">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={barData} margin={{ bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#475569' }} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Total" fill="#7c3aed" radius={[4,4,0,0]} />
              <Bar dataKey="Hot" fill="#ef4444" radius={[4,4,0,0]} />
              <Bar dataKey="Converted" fill="#10b981" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {radarData.length > 0 && (
          <ChartCard title="Performance Radar" subtitle="Top 5 counselors (normalised)">
            <ResponsiveContainer width="100%" height={240}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10, fill: '#475569' }} />
                <PolarRadiusAxis tick={{ fontSize: 9 }} domain={[0, 100]} tickCount={4} />
                {counselorStats.slice(0, 5).map((c, i) => (
                  <Radar key={c.name} name={c.name.split(' ')[0]} dataKey={c.name.split(' ')[0]}
                    stroke={COLORS[i]} fill={COLORS[i]} fillOpacity={0.12} strokeWidth={2} />
                ))}
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              </RadarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
      </div>

      {/* Leaderboard table */}
      <ChartCard title="Counselor Leaderboard" subtitle="Ranked by lead volume">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                {['Rank','Counselor','Total','Hot','Converted','Conv Rate','Top Source'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {counselorStats.map((c, i) => {
                const convRate = c.total > 0 ? ((c.converted / c.total) * 100).toFixed(1) : '0.0';
                const topSrc = Object.entries(c.sources).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';
                return (
                  <tr key={c.name} style={{ borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: i < 3 ? COLORS[i] : '#94a3b8' }}>
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0f172a' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: COLORS[i % COLORS.length] + '20', color: COLORS[i % COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                          {c.name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()}
                        </div>
                        {c.name}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#7c3aed' }}>{c.total}</td>
                    <td style={{ padding: '10px 12px', color: '#ef4444', fontWeight: 600 }}>{c.hot}</td>
                    <td style={{ padding: '10px 12px', color: '#10b981', fontWeight: 600 }}>{c.converted}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ height: 6, borderRadius: 3, background: '#f1f5f9', width: 60, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: convRate + '%', background: parseFloat(convRate) > 30 ? '#10b981' : parseFloat(convRate) > 15 ? '#f59e0b' : '#ef4444', borderRadius: 3 }} />
                        </div>
                        <span style={{ fontWeight: 600, color: '#0f172a' }}>{convRate}%</span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#64748b', fontSize: 12 }}>{topSrc}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}

// ─── SECTION: Source Analytics (ad / campaign performance) ───────────────────
function SourceAnalyticsReport({ leads, range }) {
  const days = RANGES.find(r => r.id === range)?.days || 30;
  const cutoff = Date.now() - days * 86400000;
  const filtered = useMemo(() => leads.filter(l => days >= 9999 || toTs(l.createdAt) >= cutoff), [leads, days, cutoff]);
  const fbLeads = filtered.filter(l => l.source === 'facebook');

  const sourceBreakdown = useMemo(() => countBy(filtered, 'source').map(([name, value], i) => ({
    name: name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    value, fill: COLORS[i % COLORS.length],
  })), [filtered]);

  // Campaign performance (FB)
  const campaignData = useMemo(() => {
    const map = {};
    fbLeads.forEach(l => {
      const name = l.fbCampaignName || 'Unknown Campaign';
      if (!map[name]) map[name] = { name, leads: 0, adsets: new Set(), ads: new Set() };
      map[name].leads++;
      if (l.fbAdsetName) map[name].adsets.add(l.fbAdsetName);
      if (l.fbAdName) map[name].ads.add(l.fbAdName);
    });
    return Object.values(map).map(c => ({ ...c, adsets: c.adsets.size, ads: c.ads.size })).sort((a, b) => b.leads - a.leads);
  }, [fbLeads]);

  const adsetData = useMemo(() => {
    const map = {};
    fbLeads.forEach(l => {
      const name = l.fbAdsetName || 'Unknown Ad Set';
      if (!map[name]) map[name] = { name, leads: 0 };
      map[name].leads++;
    });
    return Object.values(map).sort((a, b) => b.leads - a.leads).slice(0, 8).map((d, i) => ({ ...d, fill: COLORS[i % COLORS.length] }));
  }, [fbLeads]);

  const adData = useMemo(() => {
    const map = {};
    fbLeads.forEach(l => {
      const name = l.fbAdName || 'Unknown Ad';
      if (!map[name]) map[name] = { name, leads: 0 };
      map[name].leads++;
    });
    return Object.values(map).sort((a, b) => b.leads - a.leads).slice(0, 8).map((d, i) => ({ ...d, fill: COLORS[i % COLORS.length] }));
  }, [fbLeads]);

  // Weekly trend by top 3 sources
  const top3Sources = useMemo(() => sourceBreakdown.slice(0, 3).map(s => s.name.toLowerCase().replace(/ /g, '_')), [sourceBreakdown]);
  const weeklyBySource = useMemo(() => {
    const buckets = {};
    for (let w = 7; w >= 0; w--) {
      const t = new Date(Date.now() - w * 7 * 86400000);
      const key = 'W' + getWeekNum(t);
      buckets[key] = { week: key };
      top3Sources.forEach(s => { buckets[key][s] = 0; });
    }
    filtered.forEach(l => {
      const ts = toTs(l.createdAt);
      if (ts >= Date.now() - 56 * 86400000) {
        const d = new Date(ts);
        const key = 'W' + getWeekNum(d);
        const src = (l.source || 'unknown').toLowerCase().replace(/ /g, '_');
        if (buckets[key] && src in buckets[key]) buckets[key][src]++;
      }
    });
    return Object.values(buckets);
  }, [filtered, top3Sources]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* KPIs */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <KPICard label="FB Leads" value={fbLeads.length} sub="From Facebook Ads" color="#1877f2" icon="📘" />
        <KPICard label="Active Campaigns" value={campaignData.length} sub="With leads" color="#7c3aed" icon="📢" />
        <KPICard label="Active Ad Sets" value={adsetData.length} sub="With leads" color="#10b981" icon="🎯" />
        <KPICard label="Other Sources" value={filtered.filter(l => l.source !== 'facebook').length} sub="Non-FB leads" color="#f59e0b" icon="🌐" />
      </div>

      {/* Source pie + Weekly by source */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
        <ChartCard title="Source Distribution" subtitle="All channels">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={sourceBreakdown} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3}>
                {sourceBreakdown.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Weekly Leads by Top Sources" subtitle="8-week trend">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={weeklyBySource}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#94a3b8' }} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              {top3Sources.map((src, i) => (
                <Line key={src} type="monotone" dataKey={src} name={src.replace(/_/g, ' ')}
                  stroke={COLORS[i]} strokeWidth={2.5} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Campaign, Ad Set, Ad performance */}
      {fbLeads.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ChartCard title="Campaign Performance" subtitle="Leads per Facebook campaign">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={campaignData.slice(0,6)} layout="vertical" margin={{ left: 0, right: 20 }}>
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#475569' }} width={110} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="leads" name="Leads" radius={[0,5,5,0]}>
                    {campaignData.slice(0,6).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Ad Set Performance" subtitle="Leads per ad set">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={adsetData} layout="vertical" margin={{ left: 0, right: 20 }}>
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#475569' }} width={110} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="leads" name="Leads" radius={[0,5,5,0]}>
                    {adsetData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {adData.some(d => d.name !== 'Unknown Ad') && (
            <ChartCard title="Individual Ad Performance" subtitle="Leads per ad creative">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={adData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="leads" name="Leads" radius={[5,5,0,0]}>
                    {adData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {/* Campaign table */}
          <ChartCard title="Campaign Detail Table" subtitle="All campaigns with leads">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                    {['Campaign','Leads','Ad Sets','Ads','% of FB Total'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {campaignData.map((c, i) => (
                    <tr key={c.name} style={{ borderBottom: '1px solid #f8fafc' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0f172a' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                          {c.name}
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: '#7c3aed' }}>{c.leads}</td>
                      <td style={{ padding: '10px 12px', color: '#475569' }}>{c.adsets}</td>
                      <td style={{ padding: '10px 12px', color: '#475569' }}>{c.ads}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ height: 6, borderRadius: 3, background: '#f1f5f9', width: 70, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: (fbLeads.length > 0 ? (c.leads / fbLeads.length) * 100 : 0) + '%', background: COLORS[i % COLORS.length], borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>
                            {fbLeads.length > 0 ? ((c.leads / fbLeads.length) * 100).toFixed(1) : 0}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </>
      )}
    </div>
  );
}

// ─── SECTION: Quality Report ──────────────────────────────────────────────────
function QualityReport({ leads, pipelineStages, range }) {
  const days = RANGES.find(r => r.id === range)?.days || 30;
  const cutoff = Date.now() - days * 86400000;
  const filtered = useMemo(() => leads.filter(l => days >= 9999 || toTs(l.createdAt) >= cutoff), [leads, days, cutoff]);

  // Score distribution
  const scoreBuckets = useMemo(() => {
    const bins = { '90-100': 0, '70-89': 0, '50-69': 0, '30-49': 0, '0-29': 0 };
    filtered.forEach(l => {
      const s = l.score || 0;
      if (s >= 90) bins['90-100']++;
      else if (s >= 70) bins['70-89']++;
      else if (s >= 50) bins['50-69']++;
      else if (s >= 30) bins['30-49']++;
      else bins['0-29']++;
    });
    return Object.entries(bins).map(([range, count], i) => ({ range, count, fill: COLORS[i] }));
  }, [filtered]);

  // Response time (age when followup created — approximate)
  const withFollowup = filtered.filter(l => toTs(l.lastFollowupAt) > 0 && toTs(l.createdAt) > 0);
  const avgResponseHrs = withFollowup.length > 0
    ? (withFollowup.reduce((sum, l) => sum + (toTs(l.lastFollowupAt) - toTs(l.createdAt)), 0) / withFollowup.length / 3600000).toFixed(1)
    : '—';

  // Stale leads (no activity in 7+ days)
  const stale = filtered.filter(l => {
    const last = toTs(l.lastFollowupAt) || toTs(l.createdAt);
    return Date.now() - last > 7 * 86400000;
  });

  // Stage velocity: avg days in current stage
  const stageVelocity = useMemo(() => {
    const map = {};
    filtered.forEach(l => {
      const stage = pipelineStages.find(s => s.id === l.status || s.name === l.status);
      if (!stage) return;
      if (!map[stage.name]) map[stage.name] = { name: stage.name, count: 0, color: stage.color };
      map[stage.name].count++;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [filtered, pipelineStages]);

  // Course/program interest
  const courseData = useMemo(() => countBy(filtered.filter(l => l.course), 'course').slice(0,8).map(([name, value], i) => ({ name, value, fill: COLORS[i % COLORS.length] })), [filtered]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* KPIs */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <KPICard label="Avg Lead Score" value={filtered.length > 0 ? (filtered.reduce((s, l) => s + (l.score || 0), 0) / filtered.length).toFixed(0) : '—'} sub="Out of 100" color="#7c3aed" icon="⭐" />
        <KPICard label="High Quality (70+)" value={filtered.filter(l => (l.score || 0) >= 70).length} sub={`${filtered.length > 0 ? ((filtered.filter(l => (l.score||0) >= 70).length / filtered.length * 100).toFixed(0)) : 0}% of total`} color="#10b981" icon="✅" />
        <KPICard label="Stale Leads" value={stale.length} sub="No activity 7+ days" color="#ef4444" icon="⚠️" />
        <KPICard label="Avg Response" value={avgResponseHrs + (avgResponseHrs !== '—' ? 'h' : '')} sub="Hrs to first followup" color="#1877f2" icon="⏱️" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <ChartCard title="Lead Score Distribution" subtitle="Quality band breakdown">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={scoreBuckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="range" tick={{ fontSize: 11, fill: '#475569' }} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Leads" radius={[5,5,0,0]}>
                {scoreBuckets.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Stage Distribution" subtitle="Leads currently in each stage">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={stageVelocity} cx="50%" cy="50%" outerRadius={85} dataKey="count" paddingAngle={2}>
                {stageVelocity.map((entry, i) => <Cell key={i} fill={entry.color || COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }}
                formatter={(value) => stageVelocity.find(s => s.name === value)?.name || value} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {courseData.length > 0 && (
        <ChartCard title="Course / Program Interest" subtitle="What leads are interested in">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={courseData} layout="vertical" margin={{ left: 0, right: 30 }}>
              <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#475569' }} width={130} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" name="Leads" radius={[0,5,5,0]}>
                {courseData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
    </div>
  );
}

// ─── SECTION: Custom Report Builder ──────────────────────────────────────────
const CUSTOM_METRICS = [
  { id: 'total_leads', label: 'Lead Count', fn: (leads) => leads.length },
  { id: 'hot_leads', label: 'Hot Leads', fn: (leads) => leads.filter(l => l.priority === 'hot').length },
  { id: 'avg_score', label: 'Avg Score', fn: (leads) => leads.length > 0 ? (leads.reduce((s,l) => s+(l.score||0),0)/leads.length).toFixed(1) : 0 },
];
const CUSTOM_DIMENSIONS = [
  { id: 'source', label: 'Source', fn: (l) => l.source || 'Unknown' },
  { id: 'status', label: 'Stage', fn: (l) => l.status || 'Unknown' },
  { id: 'priority', label: 'Priority', fn: (l) => l.priority || 'Unknown' },
  { id: 'assignedTo', label: 'Counselor', fn: (l) => l.assignedTo || 'Unassigned' },
  { id: 'country', label: 'Country', fn: (l) => l.country || (l.countries?.[0]) || 'Unknown' },
  { id: 'course', label: 'Course', fn: (l) => l.course || 'Unknown' },
  { id: 'fbCampaignName', label: 'FB Campaign', fn: (l) => l.fbCampaignName || 'N/A' },
  { id: 'fbAdsetName', label: 'FB Ad Set', fn: (l) => l.fbAdsetName || 'N/A' },
  { id: 'fbAdName', label: 'FB Ad', fn: (l) => l.fbAdName || 'N/A' },
];
const CHART_TYPES = [
  { id: 'bar',        label: 'Bar',        icon: '📊' },
  { id: 'bar_h',     label: 'Bar H',       icon: '📉' },
  { id: 'line',       label: 'Line',        icon: '📈' },
  { id: 'area',       label: 'Area',        icon: '🏔️' },
  { id: 'pie',        label: 'Pie',         icon: '🥧' },
  { id: 'donut',      label: 'Donut',       icon: '🍩' },
  { id: 'table',      label: 'Table',       icon: '🗂️' },
  { id: 'scatter',    label: 'Scatter',     icon: '✦' },
];

function CustomBuilder({ leads, range }) {
  const days = RANGES.find(r => r.id === range)?.days || 30;
  const cutoff = Date.now() - days * 86400000;
  const filtered = useMemo(() => leads.filter(l => days >= 9999 || toTs(l.createdAt) >= cutoff), [leads, days, cutoff]);

  const [dimension, setDimension] = useState('source');
  const [metric, setMetric] = useState('total_leads');
  const [chartType, setChartType] = useState('bar');
  const [topN, setTopN] = useState(10);

  const dimFn = CUSTOM_DIMENSIONS.find(d => d.id === dimension)?.fn || (l => l.source);
  const metFn = CUSTOM_METRICS.find(m => m.id === metric)?.fn || (arr => arr.length);

  const data = useMemo(() => {
    const map = {};
    filtered.forEach(l => {
      const key = dimFn(l);
      if (!map[key]) map[key] = [];
      map[key].push(l);
    });
    return Object.entries(map)
      .map(([name, arr], i) => ({ name, value: Number(metFn(arr)), fill: COLORS[i % COLORS.length] }))
      .sort((a, b) => b.value - a.value)
      .slice(0, topN);
  }, [filtered, dimFn, metFn, topN]);

  const metricLabel = CUSTOM_METRICS.find(m => m.id === metric)?.label || 'Value';

  const renderChart = () => {
    if (data.length === 0) return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>No data for selected combination</div>;

    if (chartType === 'table') {
      return (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                {['#', CUSTOM_DIMENSIONS.find(d => d.id === dimension)?.label || dimension, metricLabel].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '9px 12px', color: '#94a3b8', fontWeight: 600 }}>#{i+1}</td>
                  <td style={{ padding: '9px 12px', fontWeight: 600, color: '#0f172a' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: row.fill, flexShrink: 0 }} />
                      {row.name}
                    </div>
                  </td>
                  <td style={{ padding: '9px 12px', fontWeight: 700, color: '#7c3aed' }}>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (chartType === 'pie' || chartType === 'donut') {
      return (
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={data} cx="50%" cy="50%"
              innerRadius={chartType === 'donut' ? 70 : 0}
              outerRadius={110} dataKey="value" paddingAngle={2}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              labelLine={false} style={{ fontSize: 10 }}>
              {data.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'bar_h') {
      return (
        <ResponsiveContainer width="100%" height={Math.max(200, data.length * 38)}>
          <BarChart data={data} layout="vertical" margin={{ left: 0, right: 30 }}>
            <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
            <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#475569' }} width={130} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" name={metricLabel} radius={[0,5,5,0]}>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'line') {
      return (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} />
            <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Line type="monotone" dataKey="value" name={metricLabel} stroke="#7c3aed" strokeWidth={2.5} dot={{ fill: '#7c3aed', r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'area') {
      return (
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data}>
            <defs>
              <linearGradient id="custGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.2}/>
                <stop offset="95%" stopColor="#7c3aed" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} />
            <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="value" name={metricLabel} stroke="#7c3aed" strokeWidth={2.5} fill="url(#custGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'scatter') {
      const scatterData = data.map((d, i) => ({ x: i + 1, y: d.value, name: d.name }));
      return (
        <ResponsiveContainer width="100%" height={280}>
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis type="number" dataKey="x" tick={{ fontSize: 10, fill: '#94a3b8' }} name="Rank" />
            <YAxis type="number" dataKey="y" tick={{ fontSize: 10, fill: '#94a3b8' }} name={metricLabel} allowDecimals={false} />
            <ZAxis range={[60, 200]} />
            <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
            <Scatter name={metricLabel} data={scatterData} fill="#7c3aed" fillOpacity={0.8} />
          </ScatterChart>
        </ResponsiveContainer>
      );
    }

    // Default: vertical bar
    return (
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} interval={0} angle={data.length > 6 ? -20 : 0} textAnchor={data.length > 6 ? 'end' : 'middle'} height={data.length > 6 ? 50 : 30} />
          <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="value" name={metricLabel} radius={[5,5,0,0]}>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // CSV export
  const exportCSV = () => {
    const dimLabel = CUSTOM_DIMENSIONS.find(d => d.id === dimension)?.label || dimension;
    const csv = [
      [dimLabel, metricLabel].join(','),
      ...data.map(row => ['"' + row.name + '"', row.value].join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `report_${dimension}_${metric}_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Builder controls */}
      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(0,0,0,0.07)', padding: '20px 22px', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>🔧 Build Your Report</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Group By</div>
            <select value={dimension} onChange={e => setDimension(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 9, border: '1px solid #e2e8f0', fontSize: 13, color: '#0f172a', background: '#fff', cursor: 'pointer', minWidth: 160 }}>
              {CUSTOM_DIMENSIONS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Metric</div>
            <select value={metric} onChange={e => setMetric(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 9, border: '1px solid #e2e8f0', fontSize: 13, color: '#0f172a', background: '#fff', cursor: 'pointer', minWidth: 140 }}>
              {CUSTOM_METRICS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Top N</div>
            <select value={topN} onChange={e => setTopN(Number(e.target.value))}
              style={{ padding: '8px 12px', borderRadius: 9, border: '1px solid #e2e8f0', fontSize: 13, color: '#0f172a', background: '#fff', cursor: 'pointer', width: 80 }}>
              {[5,8,10,15,20].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Chart Type</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {CHART_TYPES.map(ct => (
                <button key={ct.id} onClick={() => setChartType(ct.id)}
                  title={ct.label}
                  style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid ' + (chartType === ct.id ? '#7c3aed' : '#e2e8f0'),
                    background: chartType === ct.id ? 'rgba(124,58,237,0.1)' : '#fff', cursor: 'pointer', fontSize: 14,
                    color: chartType === ct.id ? '#7c3aed' : '#64748b' }}>
                  {ct.icon}
                </button>
              ))}
            </div>
          </div>
          <button onClick={exportCSV}
            style={{ padding: '8px 16px', borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            ⬇ Export CSV
          </button>
        </div>
      </div>

      {/* Chart output */}
      <ChartCard
        title={`${CUSTOM_METRICS.find(m => m.id === metric)?.label || metric} by ${CUSTOM_DIMENSIONS.find(d => d.id === dimension)?.label || dimension}`}
        subtitle={`Top ${topN} · ${filtered.length} leads · ${range === 'all' ? 'All time' : 'Past ' + days + ' days'}`}>
        {renderChart()}
      </ChartCard>
    </div>
  );
}

// ─── All-leads Data Table ─────────────────────────────────────────────────────
function DataTableReport({ leads, pipelineStages, range }) {
  const days = RANGES.find(r => r.id === range)?.days || 30;
  const cutoff = Date.now() - days * 86400000;
  const filtered = useMemo(() => leads.filter(l => days >= 9999 || toTs(l.createdAt) >= cutoff), [leads, days, cutoff]);

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const PER_PAGE = 20;

  const displayed = useMemo(() => {
    let arr = [...filtered];
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter(l =>
        (l.firstName || '').toLowerCase().includes(q) ||
        (l.lastName  || '').toLowerCase().includes(q) ||
        (l.email     || '').toLowerCase().includes(q) ||
        (l.phone     || '').includes(q) ||
        (l.fbCampaignName || '').toLowerCase().includes(q) ||
        (l.fbAdName  || '').toLowerCase().includes(q)
      );
    }
    arr.sort((a, b) => {
      let av = a[sortKey]; let bv = b[sortKey];
      if (sortKey === 'createdAt') { av = toTs(a.createdAt); bv = toTs(b.createdAt); }
      if (sortDir === 'asc') return av > bv ? 1 : -1;
      return av < bv ? 1 : -1;
    });
    return arr;
  }, [filtered, search, sortKey, sortDir]);

  const totalPages = Math.ceil(displayed.length / PER_PAGE);
  const pageLeads = displayed.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const exportCSV = () => {
    const headers = ['Name','Phone','Email','Source','Stage','Priority','Counselor','Country','Course','Campaign','Ad Set','Ad','Score','Date'];
    const rows = displayed.map(l => [
      ((l.firstName || '') + ' ' + (l.lastName || l.name || '')).trim(),
      l.phone || '', l.email || '', l.source || '', l.status || '', l.priority || '',
      l.assignedTo || '', l.country || (l.countries?.[0] || ''), l.course || '',
      l.fbCampaignName || '', l.fbAdsetName || '', l.fbAdName || '',
      l.score || '',
      toTs(l.createdAt) ? new Date(toTs(l.createdAt)).toLocaleDateString('en-IN') : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `leads_export_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const COLS = [
    { key: 'name', label: 'Lead' },
    { key: 'source', label: 'Source' },
    { key: 'status', label: 'Stage' },
    { key: 'priority', label: 'Priority' },
    { key: 'assignedTo', label: 'Counselor' },
    { key: 'fbCampaignName', label: 'Campaign' },
    { key: 'fbAdName', label: 'Ad' },
    { key: 'score', label: 'Score' },
    { key: 'createdAt', label: 'Date' },
  ];

  const sort = (key) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('desc'); } };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <input
          className="crm-input"
          placeholder="Search name, phone, email, campaign…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ flex: 1, height: 38, fontSize: 13 }}
        />
        <span style={{ fontSize: 12, color: '#94a3b8', flexShrink: 0 }}>{displayed.length} leads</span>
        <button onClick={exportCSV}
          style={{ padding: '8px 16px', borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          ⬇ Export CSV
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(0,0,0,0.07)', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                {COLS.map(col => (
                  <th key={col.key} onClick={() => sort(col.key)}
                    style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}>
                    {col.label} {sortKey === col.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageLeads.map((lead, i) => {
                const name = ((lead.firstName || '') + ' ' + (lead.lastName || lead.name || '')).trim() || 'Unknown';
                const stage = pipelineStages.find(s => s.id === lead.status || s.name === lead.status);
                const pColor = { hot: '#ef4444', warm: '#f59e0b', cold: '#3b82f6' }[lead.priority] || '#94a3b8';
                return (
                  <tr key={lead.id} style={{ borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 600, color: '#0f172a' }}>{name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{lead.phone || lead.email || ''}</div>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#475569', fontSize: 12 }}>{lead.source?.replace(/_/g,' ') || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {stage ? <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: (stage.color || '#64748b') + '20', color: stage.color || '#64748b' }}>{stage.name}</span> : <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: pColor, background: pColor + '15', padding: '3px 8px', borderRadius: 20 }}>
                        {lead.priority || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#475569', fontSize: 12 }}>{lead.assignedTo || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#475569', fontSize: 11, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.fbCampaignName || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#475569', fontSize: 11, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.fbAdName || '—'}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: lead.score >= 70 ? '#10b981' : lead.score >= 40 ? '#f59e0b' : '#ef4444' }}>{lead.score || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#94a3b8', fontSize: 11 }}>{toTs(lead.createdAt) ? new Date(toTs(lead.createdAt)).toLocaleDateString('en-IN') : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px 16px', borderTop: '1px solid #f1f5f9' }}>
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.1)', background: 'transparent', cursor: page === 1 ? 'default' : 'pointer', color: page === 1 ? '#cbd5e1' : '#374151', fontSize: 12 }}>‹</button>
            <span style={{ fontSize: 12, color: '#64748b' }}>Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.1)', background: 'transparent', cursor: page === totalPages ? 'default' : 'pointer', color: page === totalPages ? '#cbd5e1' : '#374151', fontSize: 12 }}>›</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN REPORTS PAGE ────────────────────────────────────────────────────────
const REPORT_TABS = [
  { id: 'overview',    label: 'Lead Overview',   icon: '📊' },
  { id: 'counselors',  label: 'Counselors',       icon: '👤' },
  { id: 'sources',     label: 'Source Analytics', icon: '📢' },
  { id: 'quality',     label: 'Lead Quality',     icon: '⭐' },
  { id: 'custom',      label: 'Custom Builder',   icon: '🔧' },
  { id: 'table',       label: 'Data Table',       icon: '🗂️' },
];

export default function Reports() {
  const { leads: allLeads, users, pipelineStages, currentUser, dataScope } = useCRM();
  const [activeTab, setActiveTab] = useState('overview');
  const [range, setRange] = useState('30d');

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  // Scope: counsellors see only their own leads in reports when setting is 'own'
  const leads = useMemo(() => {
    if (isAdmin || dataScope?.counsellorReports !== 'own') return allLeads;
    return allLeads.filter(l => l.assignedTo === currentUser?.id || l.assignedToId === currentUser?.id || l.assignedTo === currentUser?.name);
  }, [allLeads, isAdmin, dataScope?.counsellorReports, currentUser?.id, currentUser?.name]);

  // Hide counselor-comparison tab from counsellors (no point comparing to others)
  const visibleTabs = isAdmin ? REPORT_TABS : REPORT_TABS.filter(t => t.id !== 'counselors');

  return (
    <div className="page-enter" style={{ height: '100%', overflowY: 'auto', padding: '24px 28px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>Reports</h1>
          <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>Visualise lead quality, counselor performance, and source analytics</p>
        </div>
        <RangeBar range={range} setRange={setRange} />
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #f1f5f9', paddingBottom: 0 }}>
        {visibleTabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ padding: '9px 16px', borderRadius: '10px 10px 0 0', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: activeTab === tab.id ? '#fff' : 'transparent',
              color: activeTab === tab.id ? '#7c3aed' : '#64748b',
              borderBottom: activeTab === tab.id ? '2px solid #7c3aed' : '2px solid transparent',
              marginBottom: -2, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview'   && <LeadOverviewReport   leads={leads} users={users} pipelineStages={pipelineStages} range={range} />}
      {activeTab === 'counselors' && <CounselorReport      leads={leads} users={users} pipelineStages={pipelineStages} range={range} />}
      {activeTab === 'sources'    && <SourceAnalyticsReport leads={leads} range={range} />}
      {activeTab === 'quality'    && <QualityReport        leads={leads} pipelineStages={pipelineStages} range={range} />}
      {activeTab === 'custom'     && <CustomBuilder        leads={leads} range={range} />}
      {activeTab === 'table'      && <DataTableReport      leads={leads} pipelineStages={pipelineStages} range={range} />}
    </div>
  );
}
